import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  MessageCircle, Send, CheckCheck, Eye, XCircle, TrendingUp, Calendar, ArrowRight
} from "lucide-react";
import { format, subDays, startOfMonth } from "date-fns";

export default function WhatsAppOverview() {
  const { currentBranch } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [period, setPeriod] = useState("day");
  const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "yyyy-MM"));

  useEffect(() => {
    fetchStats();
  }, [period, selectedDate, selectedMonth, currentBranch]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      
      if (period === "day") {
        params.append("date", selectedDate);
      } else {
        params.append("date", selectedMonth);
      }
      
      if (currentBranch?.id) {
        params.append("branchId", currentBranch.id);
      }
      
      const res = await api.get(`/whatsapp/overview/stats?${params}`);
      setStats(res.data);
    } catch (err) {
      console.error("Failed to fetch overview stats:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (status) => {
    const filterMap = {
      total: "",
      read: "Read",
      delivered: "Delivered",
      notDelivered: "Failed",
      failed: "Failed"
    };
    const statusFilter = filterMap[status];
    if (statusFilter) {
      navigate(`/whatsapp/activity?status=${statusFilter}`);
    } else {
      navigate("/whatsapp/activity");
    }
  };

  const statCards = [
    {
      key: "total",
      label: "Total Messages",
      icon: MessageCircle,
      color: "text-foreground",
      bgColor: "bg-secondary/50",
      borderColor: "border-border"
    },
    {
      key: "read",
      label: "Read",
      icon: Eye,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
      borderColor: "border-blue-500/20"
    },
    {
      key: "delivered",
      label: "Delivered",
      icon: CheckCheck,
      color: "text-emerald-500",
      bgColor: "bg-emerald-500/10",
      borderColor: "border-emerald-500/20"
    },
    {
      key: "notDelivered",
      label: "Not Delivered",
      icon: XCircle,
      color: "text-red-500",
      bgColor: "bg-red-500/10",
      borderColor: "border-red-500/20"
    }
  ];

  return (
    <div className="space-y-6" data-testid="whatsapp-overview-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black font-heading tracking-tight uppercase">
            WhatsApp Overview
          </h1>
          <p className="text-sm text-muted-foreground">
            Message delivery statistics and engagement metrics
          </p>
        </div>
        <Button
          onClick={() => navigate("/whatsapp/messages")}
          className="font-heading uppercase tracking-wide text-xs"
          data-testid="open-chat-btn"
        >
          <MessageCircle className="h-4 w-4 mr-2" />
          Open Chat
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 flex-wrap">
            <div className="space-y-1">
              <Label className="text-[10px] font-mono uppercase">Period</Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-[140px]" data-testid="period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Day</SelectItem>
                  <SelectItem value="month">Month</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {period === "day" ? (
              <div className="space-y-1">
                <Label className="text-[10px] font-mono uppercase">Date</Label>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-[180px]"
                  data-testid="date-input"
                />
              </div>
            ) : (
              <div className="space-y-1">
                <Label className="text-[10px] font-mono uppercase">Month</Label>
                <Input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-[180px]"
                  data-testid="month-input"
                />
              </div>
            )}
            
            {/* Quick Date Buttons */}
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPeriod("day");
                  setSelectedDate(format(new Date(), "yyyy-MM-dd"));
                }}
                className="text-xs"
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPeriod("day");
                  setSelectedDate(format(subDays(new Date(), 1), "yyyy-MM-dd"));
                }}
                className="text-xs"
              >
                Yesterday
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPeriod("month");
                  setSelectedMonth(format(new Date(), "yyyy-MM"));
                }}
                className="text-xs"
              >
                This Month
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards — wrapped in a single div so space-y-6 always sees one child here */}
      <div className="mt-6">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
          </div>
        ) : stats ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {statCards.map((card) => {
              const Icon = card.icon;
              const data = stats.stats[card.key] || { count: 0, percentage: 0 };
              
              return (
                <Card
                  key={card.key}
                  className={`${card.bgColor} border ${card.borderColor} cursor-pointer transition-shadow hover:shadow-md`}
                  onClick={() => handleCardClick(card.key)}
                  data-testid={`stat-card-${card.key}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className={`text-3xl sm:text-4xl font-black font-heading ${card.color}`}>
                          {data.count.toLocaleString()}
                        </p>
                        <p className="text-[10px] sm:text-xs font-mono uppercase tracking-widest text-muted-foreground mt-1">
                          {card.label}
                        </p>
                      </div>
                      <div className={`w-12 h-12 rounded-xl ${card.bgColor} flex items-center justify-center`}>
                        <Icon className={`h-6 w-6 ${card.color}`} />
                      </div>
                    </div>
                    
                    {/* Percentage Bar */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-muted-foreground">of total</span>
                        <span className={`font-mono ${card.color}`}>{data.percentage}%</span>
                      </div>
                      <div className="h-2 bg-secondary/50 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${
                            card.key === "notDelivered" || card.key === "failed"
                              ? "bg-red-500"
                              : card.key === "read"
                              ? "bg-blue-500"
                              : card.key === "delivered"
                              ? "bg-emerald-500"
                              : "bg-primary"
                          } transition-all`}
                          style={{ width: `${data.percentage}%` }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center text-muted-foreground">
              <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>No data available</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card 
          className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer"
          onClick={() => navigate("/whatsapp/messages")}
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
              <MessageCircle className="h-6 w-6 text-green-500" />
            </div>
            <div>
              <h3 className="font-heading font-semibold">Messages</h3>
              <p className="text-xs text-muted-foreground">Chat with members</p>
            </div>
            <ArrowRight className="h-5 w-5 text-muted-foreground ml-auto" />
          </CardContent>
        </Card>

        <Card 
          className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer"
          onClick={() => navigate("/whatsapp/templates")}
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
              <Send className="h-6 w-6 text-purple-500" />
            </div>
            <div>
              <h3 className="font-heading font-semibold">Templates</h3>
              <p className="text-xs text-muted-foreground">Manage templates</p>
            </div>
            <ArrowRight className="h-5 w-5 text-muted-foreground ml-auto" />
          </CardContent>
        </Card>

        <Card 
          className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer"
          onClick={() => navigate("/whatsapp/activity")}
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-amber-500" />
            </div>
            <div>
              <h3 className="font-heading font-semibold">Activity</h3>
              <p className="text-xs text-muted-foreground">View message log</p>
            </div>
            <ArrowRight className="h-5 w-5 text-muted-foreground ml-auto" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
